import * as React from 'react';

import type { Unit } from '@rematter/frontend-lib-api-client';
import {
  Button,
  cn,
  Col,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
  Switch,
  Tab,
  Widget,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCamera,
  LucideCheck,
  LucideChevronDown,
  LucideEye,
  LucideEyeOff,
} from 'lucide-react';

import {
  ScaleCameraStream,
  type ScaleCameraStreamHandle,
} from './ScaleCameraStream';
import { ScaleWeightDisplay } from './ScaleWeightDisplay';

import { $lastFocusedRowId } from '~/modules/trade/_state/materialsInterface.state';
import { useTakeWeightEmitter } from '~/modules/trade/hooks/useScaleReading';
import { useScales } from '~/modules/trade/hooks/useScales';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

const PHOTO_CAPTURE_SUCCESS_TIMEOUT_MS = 2000;

type ScaleAndCameraWidgetProps = {
  snapshotOnWeigh?: boolean;
  onSnapshot?: (fileId: string) => void;
};

export const ScaleAndCameraWidget: React.FC<ScaleAndCameraWidgetProps> = ({
  snapshotOnWeigh,
  onSnapshot,
}) => {
  const t = useTranslations();

  const {
    allScales,
    favoriteScales,
    displayedScales,
    currentScale,
    setCurrentScaleId,
    addScaleToFavorite,
    removeScaleFromFavorite,
    canAddScaleToFavorite,
    maxFavoriteScales,
    showScaleCamera,
    setShowScaleCamera,
  } = useScales();

  const cameraStreamRef = React.useRef<ScaleCameraStreamHandle>(null);

  const focusedRowId = useAtomValue($lastFocusedRowId);

  const [photoCaptureSucceeded, setPhotoCaptureSucceeded] =
    React.useState(false);

  const { emitTakeWeightEvent } = useTakeWeightEmitter();

  const hasLineItemFocused = focusedRowId !== null;
  const hasCamera = !!currentScale?.camera;

  const handleTakeWeight = React.useCallback(
    async (weight: number, isStable: boolean, unitOfMeasurement: Unit) => {
      let snapshotFileId: string | undefined;

      if (snapshotOnWeigh && hasCamera) {
        snapshotFileId =
          (await cameraStreamRef.current?.takeSnapshot()) ?? undefined;
        if (snapshotFileId) {
          onSnapshot?.(snapshotFileId);
        }
      }

      emitTakeWeightEvent({
        weight,
        isStable,
        unitOfMeasurement,
        snapshotFileId,
      });
    },
    [snapshotOnWeigh, hasCamera, emitTakeWeightEvent, onSnapshot],
  );

  const handlePhotoCapture = React.useCallback(
    async (e: React.PointerEvent) => {
      e.preventDefault();
      if (!focusedRowId) return;

      const fileId = await cameraStreamRef.current?.takeSnapshot();
      if (!fileId) return;

      onSnapshot?.(fileId);

      setPhotoCaptureSucceeded(true);
      setTimeout(() => {
        setPhotoCaptureSucceeded(false);
      }, PHOTO_CAPTURE_SUCCESS_TIMEOUT_MS);
    },
    [focusedRowId, onSnapshot],
  );

  if (!allScales?.length) return null;

  return (
    <Widget className={cn('@ScaleAndCameraWidget', 'p-0')}>
      <Row className="justify-between border-b border-neutral/20 px-1.5">
        <Row className="w-full items-center justify-start gap-1 overflow-hidden">
          {displayedScales?.map((scaleItem) => (
            <div
              key={scaleItem.id}
              title={scaleItem.name}
              className="flex min-w-7 items-center justify-center"
            >
              <Tab
                selected={currentScale?.id === scaleItem.id}
                onClick={() => setCurrentScaleId(scaleItem.id)}
                className="px-1 py-1.5"
                variant="primary"
              >
                {scaleItem.shortName}
              </Tab>
            </div>
          ))}
        </Row>

        {(currentScale?.camera || allScales.length > maxFavoriteScales) && (
          <PopoverTrigger>
            <Button
              type="button"
              className="max-h-8.5 rounded-none border-none p-1 outline-none"
              variant="outline"
              aria-label="Scale cameras"
              icon={<LucideChevronDown className="size-4" />}
            />

            <Popover className="mr-4">
              <PopoverDialog>
                <Col className="min-w-[200px] p-0 pb-2">
                  {currentScale?.camera && (
                    <Row className="cursor-pointer items-center justify-between gap-2 bg-neutral/20 p-4 select-none">
                      <Switch
                        onChange={() => setShowScaleCamera((prev) => !prev)}
                        className="flex w-full cursor-pointer items-center gap-2"
                        isSelected={showScaleCamera}
                      >
                        {t.loadStaged?.showCamera}
                      </Switch>
                    </Row>
                  )}

                  {allScales.length > maxFavoriteScales && (
                    <div className="px-4 py-3 font-medium text-neutral/40">
                      {t.loadStaged?.visibleScales} ・{' '}
                      <span className="inline-block w-[80px] font-normal">
                        {favoriteScales.length} / {maxFavoriteScales}{' '}
                        {maxFavoriteScales === favoriteScales.length && 'max'}
                      </span>
                    </div>
                  )}

                  {allScales.map((it) => (
                    <Row
                      key={it.id}
                      className="items-center justify-between gap-3 px-4 py-2 hover:bg-neutral/10"
                    >
                      <button
                        type="button"
                        className="flex-1 cursor-pointer text-left"
                        onClick={() => {
                          setCurrentScaleId(it.id);
                        }}
                      >
                        <div className="font-medium text-neutral">
                          {it.shortName}
                        </div>
                        <div className="text-sm text-neutral/60">{it.name}</div>
                      </button>

                      {allScales.length > maxFavoriteScales && (
                        <button
                          type="button"
                          className={cn(
                            'flex cursor-pointer items-center justify-center rounded-md p-1.5 transition-colors outline-none',
                            it.isFavorite && 'bg-primary',
                            !it.isFavorite &&
                              'bg-transparent hover:bg-primary/40',
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (it.isFavorite) {
                              removeScaleFromFavorite(it.id);
                            } else if (canAddScaleToFavorite) {
                              addScaleToFavorite(it.id);
                            }
                          }}
                        >
                          {it.isFavorite ? (
                            <LucideEye size={20} className="text-white" />
                          ) : (
                            <LucideEyeOff
                              size={20}
                              className="text-neutral/40"
                            />
                          )}
                        </button>
                      )}
                    </Row>
                  ))}
                </Col>
              </PopoverDialog>
            </Popover>
          </PopoverTrigger>
        )}
      </Row>

      <Col gap2 className="p-1.5">
        <ScaleWeightDisplay
          scaleId={currentScale?.id}
          sidecar={currentScale?.sidecar}
          unitOfMeasurement={currentScale?.unitOfMeasurement}
          hasLineItemFocused={hasLineItemFocused}
          onTakeWeight={handleTakeWeight}
        />

        {showScaleCamera && currentScale?.camera && (
          <div className="relative w-full overflow-hidden rounded">
            <ScaleCameraStream
              ref={cameraStreamRef}
              cameraId={currentScale.camera.id}
              sidecarId={currentScale.camera.sidecarId}
              streamProfile={currentScale.camera.streamProfile}
              cameraSidecar={currentScale.camera.sidecar}
            />

            {hasLineItemFocused && (
              <Button
                className="absolute right-2 bottom-2 max-h-7 max-w-7 border border-white p-1.5"
                variant={photoCaptureSucceeded ? 'outline' : 'solid'}
                onPointerDown={handlePhotoCapture}
              >
                {photoCaptureSucceeded ? (
                  <LucideCheck size={12} />
                ) : (
                  <LucideCamera size={12} />
                )}
              </Button>
            )}
          </div>
        )}
      </Col>
    </Widget>
  );
};
